import { useState, useCallback, useEffect, useRef, useId } from 'react';


export interface UseSelectorOptionTooltipReturn {
  ref: React.RefObject<HTMLDivElement | null>;
  showTooltip: boolean;
  isTruncated: boolean;
  tooltipId: string;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
}

/**
 * Hook to manage truncated option tooltip logic in Selector
 * Detects when content is truncated by comparing scrollWidth vs clientWidth
 */
export function useSelectorOptionTooltip(): UseSelectorOptionTooltipReturn {
  const ref = useRef<HTMLDivElement | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const tooltipId = useId();

  const measure = useCallback(() => {
    const element = ref.current;
    if (!element) return false;
    const truncated = element.scrollWidth > element.clientWidth;
    setIsTruncated(truncated);
    return truncated;
  }, []);

  useEffect(() => {
    const requestAnimationFrameId =
      typeof window !== 'undefined' && 'requestAnimationFrame' in window
        ? window.requestAnimationFrame(() => {
          measure();
        })
        : -1;
    return () => {
      if (
        requestAnimationFrameId !== -1 &&
        typeof window !== 'undefined' &&
        'cancelAnimationFrame' in window
      ) {
        window.cancelAnimationFrame(requestAnimationFrameId);
      }
    };
  }, [measure]);

  const handleMouseEnter = useCallback(() => {
    const truncated = measure();
    if (truncated) setShowTooltip(true);
  }, [measure]);

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false);
  }, []);

  return {
    ref,
    showTooltip,
    isTruncated,
    tooltipId,
    handleMouseEnter,
    handleMouseLeave,
  };
}
